import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Question } from '@stormwater-software/lib-inspection-templates';
import React, { useContext, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import InspectionQuestionContext from '../../../contexts/InspectionQuestionContext';
import QuestionGroupContext from '../../../contexts/QuestionGroupContext';
import TemplateEditorContext from '../../../contexts/TemplateEditorContext';
import { pdfLayoutKey } from '../../../hooks/pdfLayout';
import { PdfLayoutResponse } from '../../../utils/api/pdfLayout';
import { questionApi } from '../../../utils/api/question';
import EditQuestion from './EditQuestion';
import DeleteConfirmModal from './Forms/DeleteConfirmModal';

export interface IInspectionQuestionProps {
  question: Question & { questionWidth: string };
  index: number;
  count: number;
}

const WIDTH_MIN = 10;
const WIDTH_STEP = 1;
const PERSIST_DELAY_MS = 600;

const InspectionQuestion: React.FC<IInspectionQuestionProps> = ({
  question,
  index,
  count,
}) => {
  const { questionGroup, refetchQuestionGroup } =
    useContext(QuestionGroupContext);
  const templateEditorContext = useContext(TemplateEditorContext);
  const inspectionQuestionContext = useContext(InspectionQuestionContext);
  const queryClient = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [width, setWidth] = useState(
    () => parseInt(question.questionWidth, 10) || 100,
  );
  const persistTimer = useRef<ReturnType<typeof setTimeout>>();

  const typeLabel =
    templateEditorContext.questionTypes.find(
      (qt) => qt.id === question.question_type_id,
    )?.label ?? '';

  const isForm = questionGroup.layout_type === 'FORM';

  const changeWidth = (delta: number) => {
    const next = Math.min(100, Math.max(WIDTH_MIN, width + delta));

    if (next === width || !question.id) return;

    setWidth(next);
    queryClient.setQueryData<PdfLayoutResponse | undefined>(
      pdfLayoutKey(templateEditorContext.templateId),
      (prev) =>
        prev && {
          ...prev,
          groups: prev.groups.map((group) =>
            group.id !== question.groupId
              ? group
              : {
                  ...group,
                  questions: group.questions.map((q) =>
                    q.id === question.id ? { ...q, width: next } : q,
                  ),
                },
          ),
        },
    );

    clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(
      () => questionApi.update(question.id!, { question_width: next } as any),
      PERSIST_DELAY_MS,
    );
  };

  const move = async (direction: 'up' | 'down') => {
    if (!question.id) return;

    await questionApi.update(question.id, {
      sort_order: direction === 'up' ? index - 1 : index + 2,
      groupId: question.groupId,
    });
    await refetchQuestionGroup();
  };

  return (
    <div className='utb-question'>
      <span className='utb-question__grip'>⠿</span>

      <div className='utb-question__main'>
        <span className='utb-question__label'>{question.input_label}</span>
        <span className='utb-chip utb-chip--type'>{typeLabel}</span>
      </div>

      {isForm && (
        <div className='utb-question__width'>
          <div className='utb-stepper'>
            <button
              type='button'
              disabled={width <= WIDTH_MIN}
              onClick={() => changeWidth(-WIDTH_STEP)}
            >
              <FontAwesomeIcon icon='minus' />
            </button>
            <span>{width}%</span>
            <button
              type='button'
              disabled={width >= 100}
              onClick={() => changeWidth(WIDTH_STEP)}
            >
              <FontAwesomeIcon icon='plus' />
            </button>
          </div>
          <span className='utb-question__width-bar'>
            <span style={{ width: `${width}%` }} />
          </span>
        </div>
      )}

      <span className='utb-question__actions'>
        <button type='button' disabled={index === 0} onClick={() => move('up')}>
          <FontAwesomeIcon icon='chevron-up' />
        </button>
        <button
          type='button'
          disabled={index === count - 1}
          onClick={() => move('down')}
        >
          <FontAwesomeIcon icon='chevron-down' />
        </button>
        <button type='button' onClick={() => setEdit(true)}>
          <FontAwesomeIcon icon='edit' />
        </button>
        <button
          type='button'
          className='is-danger'
          onClick={() => setShowDelete(true)}
        >
          <FontAwesomeIcon icon='trash' />
        </button>
      </span>

      {edit && question.id && (
        <EditQuestion
          questionId={question.id}
          onCancel={() => setEdit(false)}
        />
      )}

      <DeleteConfirmModal
        show={showDelete}
        title='Delete Question'
        message='Are you sure that you want to delete this question?'
        onClose={() => setShowDelete(false)}
        onConfirm={async () => {
          if (question.id) {
            await inspectionQuestionContext.deleteQuestion(question.id);
          }
          setShowDelete(false);
        }}
      />
    </div>
  );
};

export default InspectionQuestion;
